/**
 * Self-hosted Font Awesome icon definitions.
 *
 * Vendored from the organization's licensed Font Awesome Pro kit so the project
 * installs from the public npm registry alone — no Font Awesome registry token.
 * Covered by the Font Awesome Pro commercial license (see LICENSE.txt beside
 * this file); the custom `fak` glyphs are Nomic's own artwork.
 *
 * Limited to the icons the app renders: 104 definitions
 * (fab 2, fak 11, fas 3, fasl 1, fasr 5, fass 82).
 */
import type { IconPrefix } from '@fortawesome/fontawesome-svg-core'

/**
 * Font Awesome's own `IconDefinition` narrows `iconName` to its catalog, which
 * cannot describe the custom `fak` glyphs, so the vendored set is typed here.
 */
export interface VendoredIcon {
  prefix: IconPrefix
  /** Icon name, e.g. `shield-check`. */
  iconName: string
  /** `[width, height, aliases, unicode, svgPathData]` — aliases carry both name and codepoint forms. */
  icon: [number, number, (string | number)[], string, string]
}

export const icons: VendoredIcon[] = [
  {
    prefix: 'fab',
    iconName: 'microsoft',
    icon: [
      448,
      512,
      [],
      'f3ca',
      'M0 32h214.6v214.6H0V32zm233.4 0H448v214.6H233.4V32zM0 265.4h214.6V480H0V265.4zm233.4 0H448V480H233.4V265.4z'
    ]
  },
  {
    prefix: 'fab',
    iconName: 'slack',
    icon: [
      448,
      512,
      [62447, 'slack-hash'],
      'f198',
      'M94.12 315.1c0 25.9-21.16 47.06-47.06 47.06S0 341 0 315.1c0-25.9 21.16-47.06 47.06-47.06h47.06v47.06zm23.72 0c0-25.9 21.16-47.06 47.06-47.06s47.06 21.16 47.06 47.06v117.84c0 25.9-21.16 47.06-47.06 47.06s-47.06-21.16-47.06-47.06V315.1zm47.06-188.98c-25.9 0-47.06-21.16-47.06-47.06S139 32 164.9 32s47.06 21.16 47.06 47.06v47.06H164.9zm0 23.72c25.9 0 47.06 21.16 47.06 47.06s-21.16 47.06-47.06 47.06H47.06C21.16 243.96 0 222.8 0 196.9s21.16-47.06 47.06-47.06H164.9zm188.98 47.06c0-25.9 21.16-47.06 47.06-47.06 25.9 0 47.06 21.16 47.06 47.06s-21.16 47.06-47.06 47.06h-47.06V196.9zm-23.72 0c0 25.9-21.16 47.06-47.06 47.06-25.9 0-47.06-21.16-47.06-47.06V79.06c0-25.9 21.16-47.06 47.06-47.06 25.9 0 47.06 21.16 47.06 47.06V196.9zM283.1 385.88c25.9 0 47.06 21.16 47.06 47.06 0 25.9-21.16 47.06-47.06 47.06-25.9 0-47.06-21.16-47.06-47.06v-47.06h47.06zm0-23.72c-25.9 0-47.06-21.16-47.06-47.06 0-25.9 21.16-47.06 47.06-47.06h117.84c25.9 0 47.06 21.16 47.06 47.06 0 25.9-21.16 47.06-47.06 47.06H283.1z'
    ]
  },
  {
    prefix: 'fak',
    iconName: 'alert-exploit',
    icon: [
      512,
      512,
      [],
      'e001',
      'M275.8 3.3L166 134.3l96.4 81-67.3 80.2-16-13.5L45.1 441.6l66.6 55.9L245.8 337.9l-15.5-13 67.3-80.2 97.1 81.6 110-131z'
    ]
  },
  {
    prefix: 'fak',
    iconName: 'alert-malware',
    icon: [
      394,
      512,
      [],
      'e005',
      'M139.9 141.8L69 70.9l37.4-33.5 82.7 84.7 82.7-82.7 35.4 35.4-65 65L382.1 279.6v74.8H307.3L191.1 240.2 78.8 354.5H.1V281.6L139.9 141.8zM260 354.5l-68.9-68.9-68.9 68.9v78.8l68.9 68.9L260 433.2V354.5z'
    ]
  },
  {
    prefix: 'fak',
    iconName: 'alert-pua',
    icon: [
      512,
      512,
      [],
      'e002',
      'M85.3 170.7C85.3 56.9 149.3 0 277.3 0s192 56.9 192 170.7l0 341.3-96-85.3-96 85.3-96-85.3L85.3 512l0-341.3zM192 256a64 64 0 1 0 0-128 64 64 0 1 0 0 128zm170.7 0a64 64 0 1 0 0-128 64 64 0 1 0 0 128z'
    ]
  },
  {
    prefix: 'fak',
    iconName: 'alert-scan',
    icon: [
      470,
      512,
      [],
      'e003',
      'M165 87.2C66.4 127.2 16.9 238 52.8 338.3s144.6 154.3 246.3 122.5s160.1-138.1 132.5-241l34.9-9.3c32.7 122.1-37.1 248.2-158 285.1s-249.3-28.4-290.5-147.9S37.6 97.5 155.6 52.1l-9.9-37.2 41.2-11 70.3 262.5L216 277.5l-12.5-46.6c-19.8 17.8-23.8 47.4-9.3 69.8s43.1 30.9 67.5 20s37.2-37.8 30.3-63.6l34.9-9.3c12 45-12.2 91.8-55.9 107.9s-92.5-3.7-112.7-45.7s-4.9-92.5 35.1-116.4l-9.6-35.8c-59.8 29.4-86.5 100.2-61 161.8s94.3 92.8 157.4 71.4s98.7-88.1 81.5-152.5l34.9-9.3c22.4 83.6-24.6 170.1-107 196.7s-171.1-16-201.8-96.9s7.3-171.7 86.6-206.5L165 87.2z'
    ]
  },
  {
    prefix: 'fak',
    iconName: 'alert-suspicious',
    icon: [
      512,
      512,
      [],
      'e004',
      'M170.1 1.3L127.5 107.9l-41.9 105L43 234.4 .4 255.9l511.1 0L469 234.4l-42.6-21.5L384.1 107.1C370 71.7 355.9 36.3 341.6 .9c-.2-.2-19.4 18.7-42.8 42L256.4 85.3 213.6 42.5C177.4 6.3 170.7-.1 170.1 1.3zM115.2 278.3c-45.8 5.6-82.9 40-91.9 85.2c-2.1 13.6-2.1 27.4 0 41c8.5 42.5 41.7 75.7 84.2 84.2c10.1 2.1 30.9 2.1 41 0c31.9-6.4 59.1-27 74.1-55.9c8.2-14.9 12.2-31.8 11.7-48.8c0-19.2-3-31.9-11.6-48.8c-14-27-38.7-46.8-68.1-54.4c-12.8-3.2-26.1-4-39.3-2.5zm256 0c-35 4-67 26.6-82.6 58.5c-7.5 14.6-11.3 30.9-10.8 47.3c0 14.3 1.2 22.3 5.3 34.1c12.4 36.1 43 63 80.5 70.6c10.1 2.1 30.9 2.1 41 0c31.9-6.4 59.1-27 74.1-55.9c8.2-14.9 12.2-31.8 11.7-48.8c0-19.2-3-31.9-11.6-48.8c-14-27-38.7-46.8-68.1-54.4c-12.8-3.2-26.1-4-39.3-2.5zM146.3 322.7c20.1 5.9 36.8 22.5 43 42.9c1.7 5.8 2 8.6 2 18.3s-.3 12.5-2 18.3c-6.4 20.7-22.7 36.9-43.4 43.2c-5.4 1.7-8.2 2-17.9 2s-12.5-.3-17.9-2c-20.7-6.3-37-22.5-43.4-43.2c-1.7-5.8-2-8.6-2-18.3s.3-12.5 2-18.3c7.1-23.2 26.7-40.5 50.6-44.8c7.1-1.3 21.4-.3 29.1 1.9zm256 0c20.1 5.9 36.8 22.5 43 42.9c1.7 5.8 2 8.6 2 18.3s-.3 12.5-2 18.3c-6.4 20.7-22.7 36.9-43.4 43.2c-5.4 1.7-8.2 2-17.9 2s-12.5-.3-17.9-2c-20.7-6.3-37-22.5-43.4-43.2c-1.7-5.8-2-8.6-2-18.3s.3-12.5 2-18.3c7.1-23.2 26.7-40.5 50.6-44.8c7.1-1.3 21.4-.3 29.1 1.9z'
    ]
  },
  {
    prefix: 'fak',
    iconName: 'false-positive',
    icon: [
      403,
      512,
      [],
      'e00e',
      'M259.5 293l20.2-20.1-40.2-40.2-20.1 20.1L140 332.2 60.6 252.8 40.6 232.7 .4 272.9 20.4 293l79.4 79.4L20.5 451.7 .4 471.8 40.6 512l20.1-20.1L140 412.6l79.4 79.4L239.4 512l40.2-40.2-20.1-20.1-79.4-79.4L259.6 293h-.1zM372.7 38.7L353.4 56.9 150.8 248.8l-18.3 17.3-18.3-17.3L19.6 159.3 .4 141l36.6-38.7 19.3 18.3 76.3 72.2L316.9 18.3 336.1 0l36.6 38.7z'
    ]
  },
  {
    prefix: 'fak',
    iconName: 'flow-external',
    icon: [
      740,
      512,
      [],
      'e008',
      'M.2 56.9c110 0 199.1 89.1 199.1 199.1s-89.1 199.1-199.1 199.1L.2 512c141.4 0 256-114.6 256-256S141.6 0 .2 0l0 56.9zM426.9 284.4a28.4 28.4 0 1 1 0-56.9 28.4 28.4 0 1 1 0 56.9zm0-113.8c-47.1 0-85.3 38.2-85.3 85.3s38.2 85.3 85.3 85.3c37.2 0 68.8-23.7 80.5-56.9l66.6 0c11.7 33.1 43.3 56.9 80.5 56.9c47.1 0 85.3-38.2 85.3-85.3s-38.2-85.3-85.3-85.3c-37.2 0-68.8 23.7-80.5 56.9l-66.6 0c-11.7-33.1-43.3-56.9-80.5-56.9zM626 256a28.4 28.4 0 1 1 56.9 0A28.4 28.4 0 1 1 626 256z'
    ]
  },
  {
    prefix: 'fak',
    iconName: 'flow-inbound',
    icon: [
      484,
      512,
      [],
      'e006',
      'M343.4 156.5L392.6 128C348.4 51.5 265.7 0 170.9 0l0 56.9c73.7 0 138 40 172.5 99.6zM392.7 384l-49.2-28.5c-34.5 59.6-98.8 99.6-172.5 99.6l0 56.9c94.8 0 177.6-51.6 221.8-128zM125.8 227.6L191 162.3l-40.2-40.2L16.9 256 150.8 389.9 191 349.7l-65.2-65.2 358 0 0-56.9-358 0z'
    ]
  },
  {
    prefix: 'fak',
    iconName: 'flow-internal',
    icon: [
      541,
      512,
      [],
      'e007',
      'M526.5 256c0-141.4-114.6-256-256-256l0 56.9c110 0 199.1 89.1 199.1 199.1s-89.1 199.1-199.1 199.1l0 56.9c141.4 0 256-114.6 256-256zm-398.2 0a28.4 28.4 0 1 1 -56.9 0 28.4 28.4 0 1 1 56.9 0zM99.8 170.7c-47.1 0-85.3 38.2-85.3 85.3s38.2 85.3 85.3 85.3c37.2 0 68.8-23.7 80.5-56.9l66.6 0c11.7 33.1 43.3 56.9 80.5 56.9c47.1 0 85.3-38.2 85.3-85.3s-38.2-85.3-85.3-85.3c-37.2 0-68.8 23.7-80.5 56.9l-66.6 0c-11.7-33.1-43.3-56.9-80.5-56.9zM327.4 284.4a28.4 28.4 0 1 1 0-56.9 28.4 28.4 0 1 1 0 56.9z'
    ]
  },
  {
    prefix: 'fak',
    iconName: 'flow-outbound',
    icon: [
      484,
      512,
      [],
      'e009',
      'M186.9 156.5C152.5 96.9 88.1 56.9 14.4 56.9L14.4 0C109.2 0 192 51.5 236.2 128l-49.2 28.5zM236.2 384C192 460.5 109.2 512 14.4 512l0-56.9c73.7 0 138-40 172.5-99.6L236.2 384zm136.3-99.6l-329.6 0 0-56.9 329.6 0-65.2-65.2 40.2-40.2L481.3 256 347.4 389.9l-40.2-40.2 65.2-65.2z'
    ]
  },
  {
    prefix: 'fak',
    iconName: 'nomic-n-snug',
    icon: [
      640,
      512,
      [],
      'e00a',
      'M196.6 239H96v40.2H196.6v-40V239zm0 40.2V432h94.8V279.1h-95 .1zM411 80c-54.6 0-98.1 21.6-120.2 51V87.2H196.6V239h96.2c6.4-50.9 39.8-86.5 88-86.5s61 29.5 61 81.8V432H536V216.5C536 134.7 503.6 80 411.1 80H411z'
    ]
  },
  {
    prefix: 'fas',
    iconName: 'calendar',
    icon: [
      448,
      512,
      [128197, 128198],
      'f133',
      'M96 32l0 32L48 64C21.5 64 0 85.5 0 112l0 48 448 0 0-48c0-26.5-21.5-48-48-48l-48 0 0-32c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 32L160 64l0-32c0-17.7-14.3-32-32-32S96 14.3 96 32zM448 192L0 192 0 464c0 26.5 21.5 48 48 48l352 0c26.5 0 48-21.5 48-48l0-272z'
    ]
  },
  {
    prefix: 'fas',
    iconName: 'user',
    icon: [
      448,
      512,
      [62144, 128100],
      'f007',
      'M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512l388.6 0c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304l-91.4 0z'
    ]
  },
  {
    prefix: 'fas',
    iconName: 'users',
    icon: [
      640,
      512,
      [],
      'f0c0',
      'M144 0a80 80 0 1 1 0 160A80 80 0 1 1 144 0zM512 0a80 80 0 1 1 0 160A80 80 0 1 1 512 0zM0 298.7C0 239.8 47.8 192 106.7 192l42.7 0c15.9 0 31 3.5 44.6 9.7c-1.3 7.2-1.9 14.7-1.9 22.3c0 38.2 16.8 72.5 43.3 96c-.2 0-.4 0-.7 0L21.3 320C9.6 320 0 310.4 0 298.7zM405.3 320c-.2 0-.4 0-.7 0c26.6-23.5 43.3-57.8 43.3-96c0-7.6-.7-15-1.9-22.3c13.6-6.3 28.7-9.7 44.6-9.7l42.7 0C592.2 192 640 239.8 640 298.7c0 11.8-9.6 21.3-21.3 21.3l-213.3 0zM224 224a96 96 0 1 1 192 0 96 96 0 1 1 -192 0zM128 485.3C128 411.7 187.7 352 261.3 352l117.3 0C452.3 352 512 411.7 512 485.3c0 14.7-11.9 26.7-26.7 26.7l-330.7 0c-14.7 0-26.7-11.9-26.7-26.7z'
    ]
  },
  {
    prefix: 'fasl',
    iconName: 'flag-pennant',
    icon: [
      448,
      512,
      [128681, 'pennant'],
      'f456',
      'M32 16L32 0 0 0 0 16 0 32 0 64 0 320l0 32L0 496l0 16 32 0 0-16 0-145.2L403.1 209.1 448 192l-44.9-17.1L32 33.2 32 16zm0 51.5L358.3 192 32 316.5 32 67.5z'
    ]
  },
  {
    prefix: 'fasr',
    iconName: 'check',
    icon: [
      448,
      512,
      [10003, 10004],
      'f00c',
      'M448 130L431 147 177.5 399.2l-16.9 16.9-16.9-16.9L17 273.1 0 256.2l33.9-34 17 16.9L160.6 348.3 397.1 112.9l17-16.9L448 130z'
    ]
  },
  {
    prefix: 'fasr',
    iconName: 'circle',
    icon: [
      512,
      512,
      [
        9679, 9898, 9899, 11044, 61708, 61915, 128308, 128309, 128992, 128993, 128994, 128995,
        128996
      ],
      'f111',
      'M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512z'
    ]
  },
  {
    prefix: 'fasr',
    iconName: 'circle-exclamation',
    icon: [
      512,
      512,
      ['exclamation-circle'],
      'f06a',
      'M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm24-384l-48 0 0 24 0 112 0 24 48 0 0-24 0-112 0-24zM232 368l48 0 0-48-48 0 0 48z'
    ]
  },
  {
    prefix: 'fasr',
    iconName: 'circle-question',
    icon: [
      512,
      512,
      [62108, 'question-circle'],
      'f059',
      'M464 256A208 208 0 1 0 48 256a208 208 0 1 0 416 0zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm168-72c0-30.9 25.1-56 56-56l56.9 0c34.9 0 63.1 28.3 63.1 63.1c0 22.6-12.1 43.5-31.7 54.8L280 264.4l0 7.6 0 24-48 0 0-24 0-21.5 0-13.9 12.1-6.9 44.3-25.4c4.7-2.7 7.6-7.7 7.6-13.1c0-8.4-6.8-15.1-15.1-15.1L224 176c-4.4 0-8 3.6-8 8l0 6.5-48 0 0-6.5zm64 152l48 0 0 48-48 0 0-48z'
    ]
  },
  {
    prefix: 'fasr',
    iconName: 'triangle-exclamation',
    icon: [
      512,
      512,
      [9888, 'exclamation-triangle', 'warning'],
      'f071',
      'M27.4 432L0 480l55.3 0 401.4 0 55.3 0-27.4-48L283.6 80.4 256 32 228.4 80.4 27.4 432zm401.9 0L82.7 432 256 128.7 429.3 432zM232 296l0 24 48 0 0-24 0-88-48 0 0 88zm48 104l0-48-48 0 0 48 48 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'angles-up-down',
    icon: [
      448,
      512,
      [],
      'e60d',
      'M243.2 14.4L224 0 204.8 14.4l-160 120L19.2 153.6l38.4 51.2 25.6-19.2L224 80 364.8 185.6l25.6 19.2 38.4-51.2-25.6-19.2-160-120zm0 483.2l160-120 25.6-19.2-38.4-51.2-25.6 19.2L224 432 83.2 326.4 57.6 307.2 19.2 358.4l25.6 19.2 160 120L224 512l19.2-14.4z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'arrow-right',
    icon: [
      448,
      512,
      [8594],
      'f061',
      'M422.6 278.6L445.3 256l-22.6-22.6-144-144L256 66.7 210.8 112l22.6 22.6L322.8 224 32 224 0 224l0 64 32 0 290.7 0-89.4 89.4L210.8 400 256 445.3l22.6-22.6 144-144z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'arrow-up-arrow-down',
    icon: [
      576,
      512,
      ['sort-up-down'],
      'e099',
      'M438.6 454.6l96-96L557.3 336 512 290.7l-22.6 22.6L448 354.7 448 64l0-32-64 0 0 32 0 290.7-41.4-41.4L320 290.7 274.7 336l22.6 22.6 96 96L416 477.3l22.6-22.6zM182.6 57.4L160 34.7 137.4 57.4l-96 96L18.7 176 64 221.3l22.6-22.6L128 157.3 128 448l0 32 64 0 0-32 0-290.7 41.4 41.4L256 221.3 301.3 176l-22.6-22.6-96-96z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'arrow-up-right-from-square',
    icon: [
      512,
      512,
      ['external-link'],
      'f08e',
      'M320 0L288 0l0 64 32 0 82.7 0L201.4 265.4 178.7 288 224 333.3l22.6-22.6L448 109.3l0 82.7 0 32 64 0 0-32 0-160 0-32L480 0 320 0zM32 32L0 32 0 64 0 480l0 32 32 0 424 0 32 0 0-32 0-128 0-32-64 0 0 32 0 96L64 448 64 96l96 0 32 0 0-64-32 0L32 32z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'badge-check',
    icon: [
      512,
      512,
      [],
      'f336',
      'M256 0c36.8 0 68.8 20.7 84.9 51.1C373.8 41 411 49 437 75s34 63.3 23.9 96.1C491.3 187.2 512 219.2 512 256s-20.7 68.8-51.1 84.9C471 373.8 463 411 437 437s-63.3 34-96.1 23.9C324.8 491.3 292.8 512 256 512s-68.8-20.7-84.9-51.1C138.2 471 101 463 75 437s-34-63.3-23.9-96.1C20.7 324.8 0 292.8 0 256s20.7-68.8 51.1-84.9C41 138.2 49 101 75 75s63.3-34 96.1-23.9C187.2 20.7 219.2 0 256 0zM369 209l17-17L352 158.1l-17 17-111 111-47-47-17-17L126.1 256l17 17 64 64 17 17 17-17L369 209z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'ban',
    icon: [
      512,
      512,
      [128683, 'cancel'],
      'f05e',
      'M367.2 412.5L99.5 144.8C77.1 176.1 64 214.5 64 256c0 106 86 192 192 192c41.5 0 79.9-13.1 111.2-35.5zm45.3-45.3C434.9 335.9 448 297.5 448 256c0-106-86-192-192-192c-41.5 0-79.9 13.1-111.2 35.5L412.5 367.2zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'bars',
    icon: [
      448,
      512,
      ['navicon'],
      'f0c9',
      'M0 64l448 0 0 64L0 128 0 64zM0 224l448 0 0 64L0 288l0-64zM448 384l0 64L0 448l0-64 448 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'bell',
    icon: [
      448,
      512,
      [61602, 128276],
      'f0f3',
      'M256 0L192 0l0 51.2C119 66 64 130.6 64 208l0 88L0 368l0 48 448 0 0-48-64-72 0-88c0-77.4-55-142-128-156.8L256 0zm32 448l-64 0-64 0c0 17 6.7 33.3 18.7 45.3s28.3 18.7 45.3 18.7s33.3-6.7 45.3-18.7s18.7-28.3 18.7-45.3z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'bolt',
    icon: [
      448,
      512,
      [9889, 'zap'],
      'f0e7',
      'M16 288L320 0 260.7 197.6 432 224 128 512l59.3-197.6L16 288z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'book',
    icon: [
      448,
      512,
      [128212],
      'f02d',
      'M96 0C43 0 0 43 0 96L0 416c0 53 43 96 96 96l288 0 32 0 32 0 0-64-32 0 0-64 32 0L448 0 416 0 384 0 96 0zm0 384l256 0 0 64L96 448c-17.7 0-32-14.3-32-32s14.3-32 32-32zm32-256l224 0 0 32-224 0 0-32zm224 64l0 32-224 0 0-32 224 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'bridge-suspension',
    icon: [
      640,
      512,
      [],
      'e4cd',
      'M120 8L105.2 8 98.5 21.3c-21.1 42.2-56.8 71.4-96 97.9l26.9 39.8C51.7 143.9 75 126.7 96 105.4L96 256l-80 0L0 256l0 16 0 64 0 16 16 0 80 0 0 144 0 16 16 0 64 0 16 0 0-16 0-144 256 0 0 144 0 16 16 0 64 0 16 0 0-16 0-144 80 0 16 0 0-16 0-64 0-16-16 0-80 0 0-150.6c21 21.3 44.3 38.5 66.6 53.6l26.9-39.8c-39.2-26.5-74.9-55.7-96-97.9L534.8 8 520 8 504 8 490.1 8l-6.9 12.1C447.6 82.4 389.4 120 320 120s-127.6-37.6-163.2-99.9L149.9 8 136 8 120 8zm360 94.4L480 256l-40 0 0-122.1c14.4-9 27.7-19.6 40-31.6zM200 256l-40 0 0-153.6c12.3 12 25.6 22.6 40 31.6L200 256zm96 0l-48 0 0-99.5c15.3 5.1 31.3 8.5 48 10.3l0 89.2zm96 0l-48 0 0-89.2c16.7-1.7 32.7-5.2 48-10.2l0 99.5z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'briefcase',
    icon: [
      512,
      512,
      [128188],
      'f0b1',
      'M128 0l24 0L360 0l24 0 0 24 0 72 128 0 0 160-192 0-128 0L0 256 0 96l128 0 0-72 0-24zM0 288l192 0 0 64 128 0 0-64 192 0 0 192L0 480 0 288zM336 48L176 48l0 48 160 0 0-48z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'buildings',
    icon: [
      512,
      512,
      [],
      'e0cc',
      'M512 0L192 0l0 128L0 128 0 512l192 0 320 0 0-384L512 0zM64 288l64 0 0 64-64 0 0-64zm256 32l-64 0 0-64 64 0 0 64zm64 0l0-64 64 0 0 64-64 0zM128 192l0 64-64 0 0-64 64 0zM256 64l64 0 0 64-64 0 0-64zm192 0l0 64-64 0 0-64 64 0zM256 224l0-64 64 0 0 64-64 0zm192 0l-64 0 0-64 64 0 0 64z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'chart-simple',
    icon: [
      448,
      512,
      [],
      'e473',
      'M160 32l128 0 0 448-128 0 0-448zM0 224l128 0 0 256L0 480 0 224zM448 96l0 384-128 0 0-384 128 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'check',
    icon: [
      448,
      512,
      [10003, 10004],
      'f00c',
      'M447.9 142.5l-23.2 22L181 395.3l-22 20.8-22-20.8L23.2 287.6 0 265.6l44-46.5 23.2 22L159 328 380.7 118l23.2-22 44 46.5z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'chevron-down',
    icon: [
      512,
      512,
      [],
      'f078',
      'M256 429.3l22.6-22.6 192-192L493.3 192 448 146.7l-22.6 22.6L256 338.7 86.6 169.4 64 146.7 18.7 192l22.6 22.6 192 192L256 429.3z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'chevron-left',
    icon: [
      320,
      512,
      [9001],
      'f053',
      'M2.7 256l22.6 22.6 192 192L240 493.3 285.3 448l-22.6-22.6L93.3 256 262.6 86.6 285.3 64 240 18.7 217.4 41.4l-192 192L2.7 256z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'chevron-right',
    icon: [
      320,
      512,
      [9002],
      'f054',
      'M317.3 256l-22.6 22.6-192 192L80 493.3 34.7 448l22.6-22.6L226.7 256 57.4 86.6 34.7 64 80 18.7l22.6 22.6 192 192L317.3 256z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'chevron-up',
    icon: [
      512,
      512,
      [],
      'f077',
      'M256 82.7l22.6 22.6 192 192L493.3 320 448 365.3l-22.6-22.6L256 173.3 86.6 342.6 64 365.3 18.7 320l22.6-22.6 192-192L256 82.7z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'chevrons-left',
    icon: [
      512,
      512,
      ['chevron-double-left'],
      'f323',
      'M41.4 233.4L18.7 256l22.6 22.6 192 192L256 493.3 301.3 448l-22.6-22.6L109.3 256 278.6 86.6 301.3 64 256 18.7 233.4 41.4l-192 192zm192 0L210.7 256l22.6 22.6 192 192L448 493.3 493.3 448l-22.6-22.6L301.3 256 470.6 86.6 493.3 64 448 18.7 425.4 41.4l-192 192z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'chevrons-right',
    icon: [
      512,
      512,
      ['chevron-double-right'],
      'f324',
      'M278.6 278.6L301.3 256l-22.6-22.6-192-192L64 18.7 18.7 64 41.4 86.6 210.7 256 41.4 425.4 18.7 448 64 493.3l22.6-22.6 192-192zm192 0L493.3 256l-22.6-22.6-192-192L256 18.7 210.7 64l22.6 22.6L402.7 256 233.4 425.4 210.7 448 256 493.3l22.6-22.6 192-192z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'circle-1',
    icon: [
      512,
      512,
      [],
      'e0ee',
      'M0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm176 80l0 48 24 0 56 0 56 0 24 0 0-48-24 0-32 0 0-184 0-24-24 0-8 0-6.2 0-5.4 3L176 164.5l0 54.9 11.7-6.5L232 188.3 232 336l-32 0-24 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'circle-2',
    icon: [
      512,
      512,
      [],
      'e0ef',
      'M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM193.7 146.5c15.8-12 35.2-18.4 55.1-18.1l3.4 .1c46.5 .7 83.8 38.6 83.8 85.1c0 23.5-9.7 46-26.9 62.1L244.7 336l83.3 0 24 0 0 48-24 0-144 0-24 0 0-34.4 7.6-7.1L276.3 240.6c7.5-7 11.7-16.8 11.7-27.1c0-20.3-16.3-36.8-36.6-37.1l-3.4-.1c-9.1-.1-18 2.8-25.3 8.3L206.5 197l-19.1 14.5-29.1-38.2 19.1-14.5 16.2-12.3z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'circle-3',
    icon: [
      512,
      512,
      [],
      'e0f0',
      'M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm56-384l24 0 0 34.6-7.8 7.1-50.8 46.5c41.9 4.8 74.6 40.4 74.6 83.6c0 46.5-37.7 84.2-84.2 84.2l-25.2 0c-32.2 0-61.6-18.2-76-47l-4.2-8.3 42.9-21.5 4.2 8.3c6.3 12.5 19.1 20.4 33 20.4l25.2 0c20 0 36.2-16.2 36.2-36.2c0-20.1-16.3-36.3-36.4-36.2l-51.5 .3-24.1 .1 0-34.7 7.8-7.1L250.3 176 184 176l-24 0 0-48 24 0 128 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'circle-4',
    icon: [
      512,
      512,
      [],
      'e0f1',
      'M0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm320-64l-48 0 0 24 0 56-64.4 0 40-144-49.8 0L152.9 289.6 144.4 320l31.6 0 96 0 0 40 0 24 48 0 0-24 0-40 8 0 24 0 0-48-24 0-8 0 0-56 0-24z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'circle-5',
    icon: [
      512,
      512,
      [],
      'e0f2',
      'M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM208 128l104 0 24 0 0 48-24 0-82.1 0L226 218.9l56 6.7c39.9 4.8 70 38.7 70 78.9c0 43.9-35.6 79.5-79.5 79.5l-33 0c-29.8 0-57.1-16.4-71.2-42.6l-5.4-10 42.3-22.7 5.4 10c5.7 10.7 16.9 17.3 29 17.3l33 0c17.4 0 31.5-14.1 31.5-31.5c0-15.9-11.9-29.3-27.7-31.2l-79.1-9.5L174 261.1l2.1-23.2 8-88 2-21.8 21.9 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'circle-6',
    icon: [
      512,
      512,
      [],
      'e0f3',
      'M0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm256-16a48 48 0 1 1 0 96 48 48 0 1 1 0-96zm-96 48c0 53 43 96 96 96s96-43 96-96c0-49.2-37-89.8-84.7-95.3l44.4-48.4L326.6 128l-65.1 0-68.1 74.3c-21.4 23.4-33.3 54-33.3 85.7z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'circle-arrow-down',
    icon: [
      512,
      512,
      ['arrow-circle-down'],
      'f0ab',
      'M256 512A256 256 0 1 1 256 0a256 256 0 1 1 0 512zm17-103L385 297l17-17L368 246.1l-17 17-71 71L280 120l0-24-48 0 0 24 0 214.1-71-71-17-17L110.1 280l17 17L239 409l17 17 17-17z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'circle-arrow-up',
    icon: [
      512,
      512,
      ['arrow-circle-up'],
      'f0aa',
      'M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm17-409L385 215l17 17L368 265.9l-17-17-71-71L280 392l0 24-48 0 0-24 0-214.1-71 71-17 17L110.1 232l17-17L239 103l17-17 17 17z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'circle-check',
    icon: [
      512,
      512,
      [61533, 'check-circle'],
      'f058',
      'M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337l-17 17-17-17-64-64-17-17L160 222.1l17 17 47 47L335 175l17-17L385.9 192l-17 17z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'circle-info',
    icon: [
      512,
      512,
      ['info-circle'],
      'f05a',
      'M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336l24 0 0-64-24 0-24 0 0-48 24 0 48 0 24 0 0 24 0 88 8 0 24 0 0 48-24 0-80 0-24 0 0-48 24 0zm72-144l-64 0 0-64 64 0 0 64z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'circle-plus',
    icon: [
      512,
      512,
      ['plus-circle'],
      'f055',
      'M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM232 368l0-24 0-64-64 0-24 0 0-48 24 0 64 0 0-64 0-24 48 0 0 24 0 64 64 0 24 0 0 48-24 0-64 0 0 64 0 24-48 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'circle-question',
    icon: [
      512,
      512,
      [62108, 'question-circle'],
      'f059',
      'M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM168 184c0-30.9 25.1-56 56-56l56.9 0c34.9 0 63.1 28.3 63.1 63.1c0 22.6-12.1 43.5-31.7 54.8L280 264.4l0 23.6-48 0 0-24 0-13.5 0-13.9 12.1-6.9 44.3-25.4c4.7-2.7 7.6-7.7 7.6-13.1c0-8.4-6.8-15.1-15.1-15.1L224 176c-4.4 0-8 3.6-8 8l0 6.5-48 0 0-6.5zm64 184l0-48 48 0 0 48-48 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'clock',
    icon: [
      512,
      512,
      [128339, 'clock-four'],
      'f017',
      'M256 0a256 256 0 1 1 0 512A256 256 0 1 1 256 0zM232 120l0 136 0 12.8 10.7 7.1 96 64 20 13.3 26.6-39.9-20-13.3L280 243.2 280 120l0-24-48 0 0 24z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'clock-rotate-left',
    icon: [
      512,
      512,
      ['history'],
      'f1da',
      'M67.2 83.2L0 16 0 192l176 0-63.5-63.5C147.7 88.9 198.9 64 256 64c106 0 192 86 192 192s-86 192-192 192c-40.8 0-78.6-12.7-109.7-34.4l-36.6 52.5C151.2 495 201.7 512 256 512c141.4 0 256-114.6 256-256S397.4 0 256 0C181.2 0 113.9 32.1 67.2 83.2zM280 128l-48 0 0 24 0 104 0 9.9 7 7 64 64 17 17L353.9 320l-17-17-57-57 0-94.1 0-24z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'command',
    icon: [
      512,
      512,
      [],
      'e142',
      'M0 0L32 0 160 0l32 0 0 32 0 96 128 0 0-96 0-32 32 0L480 0l32 0 0 32 0 128 0 32-32 0-96 0 0 128 96 0 32 0 0 32 0 128 0 32-32 0-128 0-32 0 0-32 0-96-128 0 0 96 0 32-32 0L32 512 0 512l0-32L0 352l0-32 32 0 96 0 0-128-96 0L0 192l0-32L0 32 0 0zM128 128l0-64L64 64l0 64 64 0zm64 192l128 0 0-128-128 0 0 128zm-64 64l-64 0 0 64 64 0 0-64zm256 0l0 64 64 0 0-64-64 0zm0-256l64 0 0-64-64 0 0 64z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'computer',
    icon: [
      640,
      512,
      [],
      'e4e5',
      'M384 96l0 224L64 320 64 96l320 0zM64 32L0 32 0 96 0 320l0 64 64 0 117.3 0-10.7 32L64 416l0 64 320 0 0-64-106.7 0-10.7-32L384 384l64 0 0-64 0-224 0-64-64 0L64 32zm576 0L480 32l0 448 160 0 0-448zM544 96l32 0 16 0 0 32-16 0-32 0-16 0 0-32 16 0zm-16 64l16 0 32 0 16 0 0 32-16 0-32 0-16 0 0-32zm32 176a32 32 0 1 1 0 64 32 32 0 1 1 0-64z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'copy',
    icon: [
      448,
      512,
      [],
      'f0c5',
      'M352 0l96 96 0 288-288 0L160 0 352 0zM64 128l64 0 0 64-64 0 0 256 192 0 0-32 64 0 0 32 0 64-64 0L64 512 0 512l0-64L0 192l0-64 64 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'download',
    icon: [
      512,
      512,
      [],
      'f019',
      'M288 32l0-32L224 0l0 32 0 242.7-73.4-73.4L128 178.7 82.7 224l22.6 22.6 128 128L256 397.3l22.6-22.6 128-128L429.3 224 384 178.7l-22.6 22.6L288 274.7 288 32zM0 512l512 0 0-160-165.5 0-45.3 45.3L256 442.5l-45.3-45.3L165.5 352 0 352 0 512zM432 408a24 24 0 1 1 0 48 24 24 0 1 1 0-48z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'ellipsis-vertical',
    icon: [
      128,
      512,
      ['ellipsis-v'],
      'f142',
      'M16 48l96 0 0 96-96 0 0-96zm0 160l96 0 0 96-96 0 0-96zm96 160l0 96-96 0 0-96 96 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'envelope',
    icon: [
      512,
      512,
      [9993, 61443, 128386],
      'f0e0',
      'M0 64l512 0 0 80L256 320 0 144 0 64zM0 448L0 182.8 237.9 346.4 256 358.8l18.1-12.5L512 182.8 512 448 0 448z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'eye',
    icon: [
      576,
      512,
      [128065],
      'f06e',
      'M288 32C129.6 32 30 181.3 0 256c30 74.7 129.6 224 288 224s258-149.3 288-224C546 181.3 446.4 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm48 0c0 53 43 96 96 96s96-43 96-96s-43-96-96-96c-6.4 0-12.7 .6-18.8 1.8L288 256l-94.2-18.8c-1.2 6.1-1.8 12.4-1.8 18.8z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'eye-slash',
    icon: [
      640,
      512,
      [],
      'f070',
      'M48.4 14.8L29.4 .1 0 38 19 52.7 591.5 497.2l19 14.7L639.9 474l-19-14.7L524 384.1c41.9-44 70.2-93.9 84-128.1C578 181.3 478.4 32 320 32c-66.9 0-123.2 26.6-168.3 63L48.4 14.8zM222.5 150c25.6-23.6 59.9-38 97.5-38c79.5 0 144 64.5 144 144c0 24.7-6.2 47.9-17.1 68.2l-38.7-30.1c5.1-11.7 7.9-24.6 7.9-38.1c0-53-43-96-96-96c-6.4 0-12.7 .6-18.8 1.8l11.6 58.2L222.5 150zM444.9 446.6L373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L85.7 163.5C60.2 197.1 42.1 230.8 32 256c30 74.7 129.6 224 288 224c46.9 0 88.6-13.1 124.9-33.4zM324.8 351.9l-99.1-78.1C234 318.3 273.1 352 320 352c1.6 0 3.2 0 4.8-.1z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'file-code',
    icon: [
      384,
      512,
      [],
      'f1c9',
      'M224 0L0 0 0 512l384 0 0-352-160 0L224 0zm32 0l0 128 128 0L256 0zM153 289l-31 31 31 31 17 17L136 401.9l-17-17L71 337l-17-17 17-17 48-48 17-17L169.9 272l-17 17zM265 255l48 48 17 17-17 17-48 48-17 17L214.1 368l17-17 31-31-31-31-17-17L248 238.1l17 17z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'file-csv',
    icon: [
      512,
      512,
      [],
      'f6dd',
      'M0 0L224 0l0 160 160 0 0 144-272 0 0 208L0 512 0 0zM384 128l-128 0L256 0 384 128zM200 352l16 0c22.1 0 40 17.9 40 40l0 8 0 16-32 0 0-16 0-8c0-4.4-3.6-8-8-8l-16 0c-4.4 0-8 3.6-8 8l0 80c0 4.4 3.6 8 8 8l16 0c4.4 0 8-3.6 8-8l0-8 0-16 32 0 0 16 0 8c0 22.1-17.9 40-40 40l-16 0c-22.1 0-40-17.9-40-40l0-80c0-22.1 17.9-40 40-40zm133.1 0l34.9 0 16 0 0 32-16 0-34.9 0c-7.2 0-13.1 5.9-13.1 13.1c0 5.2 3 9.9 7.8 12l37.4 16.6c16.3 7.2 26.8 23.4 26.8 41.2c0 24.9-20.2 45.1-45.1 45.1L304 512l-16 0 0-32 16 0 42.9 0c7.2 0 13.1-5.9 13.1-13.1c0-5.2-3-9.9-7.8-12l-37.4-16.6c-16.3-7.2-26.8-23.4-26.8-41.2c0-24.9 20.2-45.1 45.1-45.1zM448 352l0 16 0 35c0 17.3 3.1 34.4 9.2 50.6l6.8 18.2 6.8-18.2c6.1-16.2 9.2-33.3 9.2-50.6l0-35 0-16 32 0 0 16 0 35c0 21.1-3.8 42-11.2 61.8L487 501.6 483.1 512 472 512l-16 0-11.1 0L441 501.6l-13.8-36.8C419.8 445 416 424.1 416 403l0-35 0-16 32 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'file-export',
    icon: [
      576,
      512,
      ['arrow-right-from-file'],
      'f56e',
      'M0 0L224 0l0 160 160 0 0 128-168 0-24 0 0 48 24 0 168 0 0 176L0 512 0 0zM384 336l0-48 110.1 0-39-39-17-17L472 198.1l17 17 80 80 17 17-17 17-80 80-17 17L438.1 392l17-17 39-39L384 336zm0-208l-128 0L256 0 384 128z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'file-pdf',
    icon: [
      512,
      512,
      [],
      'f1c1',
      'M0 0L224 0l0 160 160 0 0 144-272 0 0 208L0 512 0 0zM384 128l-128 0L256 0 384 128zM176 352l32 0c30.9 0 56 25.1 56 56s-25.1 56-56 56l-16 0 0 32 0 16-32 0 0-16 0-48 0-80 0-16 16 0zm32 80c13.3 0 24-10.7 24-24s-10.7-24-24-24l-16 0 0 48 16 0zm96-80l32 0c26.5 0 48 21.5 48 48l0 64c0 26.5-21.5 48-48 48l-32 0-16 0 0-16 0-128 0-16 16 0zm32 128c8.8 0 16-7.2 16-16l0-64c0-8.8-7.2-16-16-16l-16 0 0 96 16 0zm80-128l16 0 48 0 16 0 0 32-16 0-32 0 0 32 32 0 16 0 0 32-16 0-32 0 0 48 0 16-32 0 0-16 0-64 0-64 0-16z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'files',
    icon: [
      448,
      512,
      [],
      'e178',
      'M320 96l0 32 32 0 96 0 0 288L96 416 96 0 320 0l0 96zm128 0l-45.3 0L384 96l-32 0 0-32 0-18.7L352 0l32 32 32 32 32 32zM48 120l0 344 280 0 24 0 0 48-24 0L24 512 0 512l0-24L0 120 0 96l48 0 0 24z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'flag',
    icon: [
      448,
      512,
      [61725, 127988],
      'f024',
      'M64 32L64 0 0 0 0 32 0 64 0 368 0 480l0 32 64 0 0-32 0-128 64.3-16.1c41.1-10.3 84.6-5.5 122.5 13.4c44.2 22.1 95.5 24.8 141.7 7.4L448 336l0-320L393.6 43.2c-46.3 23.2-100.8 23.2-147.1 0c-35.1-17.6-75.4-22-113.5-12.5L64 48l0-16z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'flag-pennant',
    icon: [
      448,
      512,
      [128681, 'pennant'],
      'f456',
      'M64 32L64 0 0 0 0 32 0 480l0 32 64 0 0-32 0-128L448 192 64 32z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'floppy-disk',
    icon: [
      448,
      512,
      [128190, 128426, 'save'],
      'f0c7',
      'M32 32L0 32 0 64 0 448l0 32 32 0 384 0 32 0 0-32 0-288 0-13.3-9.4-9.4-96-96L333.3 32 320 32 32 32zM64 96l256 0 0 128L64 224 64 96zM224 288a64 64 0 1 1 0 128 64 64 0 1 1 0-128z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'gear',
    icon: [
      512,
      512,
      [9881, 'cog'],
      'f013',
      'M200 0L312 0l17.2 78.4c15.8 6.5 30.6 15.1 44 25.4l76.5-24.4 56 97-59.4 54.1c1.1 8.3 1.7 16.8 1.7 25.4s-.6 17.1-1.7 25.4l59.4 54.1-56 97-76.5-24.4c-13.4 10.3-28.2 18.9-44 25.4L312 512l-112 0-17.2-78.4c-15.8-6.5-30.6-15.1-44-25.4L62.3 432.5l-56-97 59.4-54.1C64.6 273.1 64 264.6 64 256s.6-17.1 1.7-25.4L6.3 176.5l56-97 76.5 24.4c13.4-10.3 28.2-18.9 44-25.4L200 0zm56 336a80 80 0 1 0 0-160 80 80 0 1 0 0 160z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'globe',
    icon: [
      512,
      512,
      [127760],
      'f0ac',
      'M352 256c0 22.1-2.2 43.5-6.1 64l-179.7 0c-3.9-20.5-6.1-41.9-6.1-64s2.2-43.5 6.1-64l179.7 0c3.9 20.5 6.1 41.9 6.1 64zm26.4-64l125.6 0c5.3 20.5 8.1 41.9 8.1 64s-2.8 43.5-8.1 64l-125.6 0c3.6-20.5 5.6-41.9 5.6-64s-2-43.5-5.6-64zm115-32l-122 0c-17-65.6-48.3-120-74-156.7c89.2 14.5 163 75.2 196 156.7zm-155.1 0l-164.6 0c11.4-40.2 28.4-75.9 45.8-105.4c13-21.9 25.9-40 36.5-53.5c10.5 13.5 23.5 31.6 36.5 53.5c17.4 29.4 34.5 65.1 45.8 105.4zm-197.7 0l-122 0c33-81.5 106.7-142.2 196-156.7c-25.7 36.7-57 91-74 156.7zM8.1 192l125.6 0c-3.6 20.5-5.6 41.9-5.6 64s2 43.5 5.6 64L8.1 320C2.8 299.5 0 278.1 0 256s2.8-43.5 8.1-64zM173.7 352l164.6 0c-11.4 40.2-28.4 75.9-45.8 105.4c-13 21.9-25.9 40-36.5 53.5c-10.5-13.5-23.5-31.6-36.5-53.5c-17.4-29.4-34.5-65.1-45.8-105.4zm-33.2 0c17 65.6 48.3 120 74 156.7c-89.2-14.5-163-75.2-196-156.7l122 0zm352.8 0c-33 81.5-106.7 142.2-196 156.7c25.7-36.7 57-91 74-156.7l122 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'headset',
    icon: [
      512,
      512,
      [],
      'f590',
      'M256 48C141.1 48 48 141.1 48 256l0 40 0 24L0 320l0-24 0-40C0 114.6 114.6 0 256 0S512 114.6 512 256l0 208.1 0 24-24 0L320 488l0 24-80 0c-26.5 0-48-21.5-48-48s21.5-48 48-48l80 0 0 24 144 .1L464 256c0-114.9-93.1-208-208-208zM192 224l0 160L80 384l0-160 112 0zm240 0l0 160-112 0 0-160 112 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'house',
    icon: [
      576,
      512,
      [63498, 63500, 127968, 'home', 'home-alt', 'home-lg-alt'],
      'f015',
      'M511.8 287.6l64.2 0 0-47.6L288.4 0 0 240l0 47.6 64.1 0 0 224.4L224 512l0-160 128 0 0 160 160.8 0-1-224.4z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'life-ring',
    icon: [
      512,
      512,
      [],
      'f1cd',
      'M367.2 412.5C335.9 434.9 297.5 448 256 448s-79.9-13.1-111.2-35.5l58-58c15.8 8.6 34 13.5 53.3 13.5s37.4-4.9 53.3-13.5l58 58zm91 .5c33.7-43.3 53.8-97.8 53.8-157s-20.1-113.6-53.8-157l19-19L432 34.7l-19 19C369.6 20.1 315.2 0 256 0S142.4 20.1 99 53.8l-19-19L34.7 80l19 19C20.1 142.4 0 196.8 0 256s20.1 113.6 53.8 157l-19 19L80 477.3l19-19c43.3 33.7 97.8 53.8 157 53.8s113.6-20.1 157-53.8l19 19L477.3 432l-19-19zm-45.7-45.7l-58-58c8.6-15.8 13.5-34 13.5-53.3s-4.9-37.4-13.5-53.3l58-58C434.9 176.1 448 214.5 448 256s-13.1 79.9-35.5 111.2zM367.2 99.5l-58 58c-15.8-8.6-34-13.5-53.3-13.5s-37.4 4.9-53.3 13.5l-58-58C176.1 77.1 214.5 64 256 64s79.9 13.1 111.2 35.5zM157.5 309.3l-58 58C77.1 335.9 64 297.5 64 256s13.1-79.9 35.5-111.2l58 58c-8.6 15.8-13.5 34-13.5 53.3s4.9 37.4 13.5 53.3zM208 256a48 48 0 1 1 96 0 48 48 0 1 1 -96 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'link',
    icon: [
      640,
      512,
      [128279, 'chain'],
      'f0c1',
      'M580.2 267.3c56.2-56.2 56.2-147.4 0-203.6S432.8 7.4 376.6 63.7L365.3 75l45.3 45.3 11.3-11.3c31.2-31.2 81.9-31.2 113.1 0s31.2 81.9 0 113.1L421.8 335.2c-31.2 31.2-81.9 31.2-113.1 0c-25.6-25.6-30.3-64.3-13.8-94.6c1.8-3.4 3.9-6.7 6.3-9.8l-51.2-38.4c-4.3 5.7-8.1 11.6-11.4 17.8c-29.5 54.6-21.3 124.2 24.9 170.3c56.2 56.2 147.4 56.2 203.6 0L580.2 267.3zM59.8 244.7c-56.2 56.2-56.2 147.4 0 203.6s147.4 56.2 203.6 0L274.7 437l-45.3-45.3-11.3 11.3c-31.2 31.2-81.9 31.2-113.1 0s-31.2-81.9 0-113.1L218.2 176.8c31.2-31.2 81.9-31.2 113.1 0c25.6 25.6 30.3 64.3 13.8 94.6c-1.8 3.4-3.9 6.7-6.3 9.8l51.2 38.4c4.3-5.7 8.1-11.6 11.4-17.8c29.5-54.6 21.3-124.2-24.9-170.3c-56.2-56.2-147.4-56.2-203.6 0L59.8 244.7z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'list',
    icon: [
      512,
      512,
      ['list-squares'],
      'f03a',
      'M112 48L16 48l0 96 96 0 0-96zm80 16l-32 0 0 64 32 0 288 0 32 0 0-64-32 0L192 64zm0 160l-32 0 0 64 32 0 288 0 32 0 0-64-32 0-288 0zm0 160l-32 0 0 64 32 0 288 0 32 0 0-64-32 0-288 0zM16 208l0 96 96 0 0-96-96 0zm96 160l-96 0 0 96 96 0 0-96z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'location-dot',
    icon: [
      384,
      512,
      ['map-marker-alt'],
      'f3c5',
      'M192 512s192-208 192-320C384 86 298 0 192 0S0 86 0 192C0 304 192 512 192 512zm0-384a64 64 0 1 1 0 128 64 64 0 1 1 0-128z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'magnifying-glass',
    icon: [
      512,
      512,
      [128269, 'search'],
      'f002',
      'M416 208c0 45.9-14.9 88.3-40 122.7L486.6 441.4 509.3 464 464 509.3l-22.6-22.6L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'minus',
    icon: [
      448,
      512,
      [8211, 8722, 10134, 'subtract'],
      'f068',
      'M432 288l-32 0L48 288l-32 0 0-64 32 0 352 0 32 0 0 64z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'network-wired',
    icon: [
      640,
      512,
      [],
      'f6ff',
      'M384 64l0 64-32 0-64 0-32 0 0-64 128 0zM352 192l32 0 64 0 0-64 0-64 0-64L384 0 256 0 192 0l0 64 0 64 0 64 64 0 32 0 0 32-96 0-64 0-96 0L0 224l0 64 32 0 96 0 0 32-32 0-64 0 0 64 0 64 0 64 64 0 128 0 64 0 0-64 0-64 0-64-64 0-32 0 0-32 96 0 64 0 96 0 0 32-32 0-64 0 0 64 0 64 0 64 64 0 128 0 64 0 0-64 0-64 0-64-64 0-32 0 0-32 96 0 32 0 0-64-32 0-96 0-64 0-96 0 0-32zM512 384l32 0 0 64-128 0 0-64 32 0 64 0zm-320 0l32 0 0 64L96 448l0-64 32 0 64 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'paper-plane-top',
    icon: [
      512,
      512,
      ['paper-plane-alt', 'send'],
      'e20a',
      'M83.4 226.6L304 256 83.4 285.4 0 480l64 0L512 256 64 32 0 32 83.4 226.6z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'pencil',
    icon: [
      512,
      512,
      [9999, 61504, 'pencil-alt'],
      'f303',
      'M10.2 461L0 512l51-10.2L160 480 420.7 219.3l-16-16-96-96-16-16L32 352 10.2 461zM315.3 68.7l16 16 96 96 16 16 34.7-34.7L512 128 478.1 94.1 417.9 33.9 384 0 350.1 33.9 315.3 68.7zM99.9 352l12.1 0 0 48 48 0 0 12.1-23.7 23.7-75.1 15 15-75.1L99.9 352zM326.6 176l-11.3 11.3-144 144L160 342.6 137.4 320l11.3-11.3 144-144L304 153.4 326.6 176z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'people-group',
    icon: [
      640,
      512,
      [],
      'e533',
      'M128 32a56 56 0 1 1 0 112 56 56 0 1 1 0-112zM53.3 275.1L64 292.4l0-45.1L53.3 275.1zM0 280L40 176l172.3 0-.1 .1-64 112L138 305.9 150 322.6l42 58.9 0 2.5 0 64L64 448l0-64L0 280zM448 381.5l42-58.9L502 305.9l-10.2-17.8-64-112-.1-.1L600 176l40 104L576 384l0 64-128 0 0-64 0-2.5zM512 32a56 56 0 1 1 0 112 56 56 0 1 1 0-112zm74.7 243.1L576 247.3l0 45.1 10.7-17.3zM320 32a64 64 0 1 1 0 128 64 64 0 1 1 0-128zM232.9 301.1L256 333.4l0-72.7-23.1 40.4zM384 260.7l0 72.7 23.1-32.3L384 260.7zM464 304L384 416l0 64-128 0 0-64L176 304l64-112 160 0 64 112z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'plus',
    icon: [
      448,
      512,
      [10133, 61543, 'add'],
      '2b',
      'M256 80l0-32-64 0 0 32 0 144L48 224l-32 0 0 64 32 0 144 0 0 144 0 32 64 0 0-32 0-144 144 0 32 0 0-64-32 0-144 0 0-144z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'right-from-bracket',
    icon: [
      512,
      512,
      ['sign-out-alt'],
      'f2f5',
      'M352 96L512 256 352 416l-32 0 0-96-160 0 0-128 160 0 0-96 32 0zM160 96L64 96l0 320 96 0 32 0 0 64-32 0L32 480 0 480l0-32L0 64 0 32l32 0 128 0 32 0 0 64-32 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'rotate-left',
    icon: [
      512,
      512,
      ['rotate-back', 'rotate-backward', 'undo-alt'],
      'f2ea',
      'M142.9 142.9L200 200l-24 24L16 224 16 64 40 40 97.6 97.6s0 0 0 0c87.5-87.5 229.3-87.5 316.8 0s87.5 229.3 0 316.8s-229.3 87.5-316.8 0l45.3-45.3c62.5 62.5 163.8 62.5 226.3 0s62.5-163.8 0-226.3s-163.8-62.5-226.3 0c0 0 0 0 0 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'rss',
    icon: [
      448,
      512,
      ['feed'],
      'f09e',
      'M448 480C448 232.6 247.4 32 0 32L0 96c212.1 0 384 171.9 384 384l64 0zM0 160l0 64c141.4 0 256 114.6 256 256l64 0C320 303.3 176.7 160 0 160zM128 416A64 64 0 1 0 0 416a64 64 0 1 0 128 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'server',
    icon: [
      512,
      512,
      [],
      'f233',
      'M512 32L0 32 0 224l512 0 0-192zM344 104a24 24 0 1 1 0 48 24 24 0 1 1 0-48zm48 24a24 24 0 1 1 48 0 24 24 0 1 1 -48 0zM512 288L0 288 0 480l512 0 0-192zM344 360a24 24 0 1 1 0 48 24 24 0 1 1 0-48zm56 24a24 24 0 1 1 48 0 24 24 0 1 1 -48 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'shield-check',
    icon: [
      512,
      512,
      [],
      'f2f7',
      'M256 5.7l11.5 4.4L475 90.2l19.2 7.4 1.2 20.6c2.9 49.7-4.9 125.6-37.3 199.8C425.4 392.8 367 467.1 268.6 509.4L256 514.8l-12.6-5.4C145 467.1 86.6 392.8 53.9 318C21.4 243.7 13.6 167.8 16.6 118.1l1.2-20.6L37 90.2l207.5-80L256 5.7zM369 209l17-17L352 158.1l-17 17-111 111-47-47-17-17L126.1 256l17 17 64 64 17 17 17-17L369 209z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'shield-halved',
    icon: [
      512,
      512,
      ['shield-alt'],
      'f3ed',
      'M267.5 10.1L256 5.7l-11.5 4.4L37 90.2 17.8 97.6l-1.2 20.6C13.6 167.8 21.4 243.7 53.9 318C86.6 392.8 145 467.1 243.4 509.4l12.6 5.4 12.6-5.4C367 467.1 425.4 392.8 458.1 318c32.5-74.2 40.3-150.2 37.3-199.8l-1.2-20.6L475 90.2l-207.5-80zM256 74.3s0 0 0 0l176 67.9c-.5 40.6-8.8 96-32.5 150.2C373.3 352.2 328.9 409.5 256 444.8l0-370.5z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'signal-stream',
    icon: [
      576,
      512,
      [],
      'f8dd',
      'M200.1 186.6l-50.2-39.7C126.2 176.9 112 214.8 112 256s14.2 79.1 37.9 109.1l50.2-39.7C185 306.3 176 282.2 176 256s9-50.3 24.1-69.4zm-87.9-69.4L61.9 77.5C23.2 126.6 0 188.6 0 256s23.2 129.4 61.9 178.5l50.2-39.7C82 356.6 64 308.5 64 256s18-100.6 48.2-138.8zM375.9 325.4l50.2 39.7c23.7-30 37.9-67.9 37.9-109.1s-14.2-79.1-37.9-109.1l-50.2 39.7C391 205.7 400 229.8 400 256s-9 50.3-24.1 69.4zm87.9 69.4l50.2 39.7C552.8 385.4 576 323.4 576 256s-23.2-129.4-61.9-178.5l-50.2 39.7C494 155.4 512 203.5 512 256s-18 100.6-48.2 138.8zM288 312a56 56 0 1 0 0-112 56 56 0 1 0 0 112z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'sparkles',
    icon: [
      512,
      512,
      [10024],
      'f890',
      'M384 64L416 0l32 64 64 32-64 32-32 64-32-64L320 96l64-32zM128 192L192 64l64 128 128 64L256 320 192 448 128 320 0 256l128-64zM416 320l32 64 64 32-64 32-32 64-32-64-64-32 64-32 32-64z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'spinner-third',
    icon: [
      512,
      512,
      [],
      'f3f4',
      'M477.7 384c21.8-37.7 34.3-81.4 34.3-128C512 114.6 397.4 0 256 0l0 64c106 0 192 86 192 192c0 35-9.4 67.8-25.7 96l55.4 32z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'star',
    icon: [
      576,
      512,
      [11088, 61446],
      'f005',
      'M288.1 0l86.5 164 182.7 31.6L428 328.5 454.4 512 288.1 430.2 121.7 512l26.4-183.5L18.9 195.6 201.5 164 288.1 0z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'swords',
    icon: [
      512,
      512,
      [9876],
      'f71d',
      'M16 96L0 0 96 16 393.4 313.4l-80 80L16 96zM408 456l-72 24-32-32L448 304l32 32-24 72 56 56-48 48-56-56zM96 336l57.4-57.4 80 80L176 416l32 32-32 32-72-24L48 512 0 464l56-56L32 336l32-32 32 32zM496 96L358.6 233.4l-80-80L416 16 512 0 496 96z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'trash',
    icon: [
      448,
      512,
      [],
      'f1f8',
      'M144 0L128 32 0 32 0 96l448 0 0-64L320 32 304 0 144 0zM416 128L32 128 56 512l336 0 24-384z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'triangle-exclamation',
    icon: [
      512,
      512,
      [9888, 'exclamation-triangle', 'warning'],
      'f071',
      'M256 32L0 480l512 0L256 32zm24 160l0 24 0 112 0 24-48 0 0-24 0-112 0-24 48 0zM232 384l48 0 0 48-48 0 0-48z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'volume-high',
    icon: [
      640,
      512,
      [128266, 'volume-up'],
      'f028',
      'M592 256c0 78.5-37.7 148.2-96 192l28.8 38.4C594.7 433.9 640 350.3 640 256.1l0-.1 0-.1c0-94.2-45.3-177.8-115.2-230.3L496 64c58.3 43.8 96 113.5 96 192zM438.4 371.2l28.8 38.4c46.6-35 76.8-90.8 76.8-153.6s-30.2-118.6-76.8-153.6l-28.8 38.4c35 26.3 57.6 68.1 57.6 115.2s-22.6 88.9-57.6 115.2zM400 256c0 15.7-7.5 29.6-19.2 38.4l28.8 38.4C432.9 315.3 448 287.4 448 256s-15.1-59.3-38.4-76.8l-28.8 38.4c11.7 8.8 19.2 22.7 19.2 38.4zM0 160L0 352l128 0L272 480l48 0 0-448-48 0L128 160 0 160z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'volume-xmark',
    icon: [
      576,
      512,
      ['volume-mute', 'volume-times'],
      'f6a9',
      'M0 160L0 352l128 0L272 480l48 0 0-448-48 0L128 160 0 160zm441 23l-17-17L390.1 200l17 17 39 39-39 39-17 17L424 345.9l17-17 39-39 39 39 17 17L569.9 312l-17-17-39-39 39-39 17-17L536 166.1l-17 17-39 39-39-39z'
    ]
  },
  {
    prefix: 'fass',
    iconName: 'xmark',
    icon: [
      384,
      512,
      [215, 10005, 10006, 10060, 128473, 'close', 'multiply', 'remove', 'times'],
      'f00d',
      'M326.6 166.6L349.3 144 304 98.7l-22.6 22.6L192 210.7l-89.4-89.4L80 98.7 34.7 144l22.6 22.6L146.7 256 57.4 345.4 34.7 368 80 413.3l22.6-22.6L192 301.3l89.4 89.4L304 413.3 349.3 368l-22.6-22.6L237.3 256l89.4-89.4z'
    ]
  }
]
